import { featuresExtent, mapRotationDegrees } from '@openlayers/ol-utils';
import { TileLayersDropdown } from '@openlayers/tile-layers/ui/TileLayersDropdown';
import { MapToolsButtons } from '@openlayers/tools/MapToolsButtons';
import { LogpointsEditor2dMap } from '@pages/map/actions/LogpointsEditor2dMap';
import { mapRootStore, mapStateFacade, mapTools, mapUI, mapWrapper } from '@pages/map/map';
import { SaveMapImageDialog } from '@pages/map/tools/SaveMapImageDialog';
import { SelectionTool } from '@pages/map/tools/tool-selection';
import { Cache } from '@shared/Cache';
import { Button } from '@shared/ui-react/Button';
import { DialogUtils } from '@shared/ui-react/Dialog';
import { Dropdown } from '@shared/ui-react/Dropdown';
import { PageStateUtils } from '@shared/user-state/PageStateUtils';
import { cs } from '@shared/utils/utils-classname';
import { observer } from 'mobx-react';
import OlMap from 'ol/Map';
import * as olExtent from 'ol/extent';
import { Geometry, GeometryCollection } from 'ol/geom';
import * as React from 'react';
import { Notify } from '@shared/ui-native/Notify';
import { Telemetry, TelemetryEventType, TelemetrySource } from '@shared/Telemetry';
import { WindowManager } from '@shared/ui-react/FloatingWindow';

@observer
export class Map2dToolbar extends React.Component {
    render() {
        return (
            <>
                <MapToolsButtons
                    toolsStore={mapTools}
                    treeData={mapRootStore.treeData}
                    openCrossSectionWindow={() => mapTools.openCrossSectionWindow(mapUI.stationAndModelInfoBox.stationInfo.station, null, null, null)}
                    openLongSectionWindow={() => mapTools.openLongSectionWindow(null, null)}
                />
                <div className='btn-group'>
                    <Button
                        secondary
                        id='tool-approval'
                        icon='iki iki-approve'
                        onClick={this.startApproval}
                        title={__('js_map_measuring_tools.approve_logpoints')}
                        className={cs({ active: mapTools.activeTool instanceof SelectionTool })}
                    />
                </div>
                <div className='btn-group' onClick={this.fitVisible}>
                    <Button
                        secondary
                        id='fit-project-btn'
                        title='keyboard: ctrl + a'
                        icon='iki iki-fit-to-screen'
                        tooltip={__('js_map.fit_visible')}
                        className='toolbar-optional-label'
                    ></Button>
                </div>
                <div className='btn-group' onClick={this.openIn3d}>
                    <Button
                        secondary
                        title={__('js_saved_views.open_in_[2d_or_3d]_map', window.location.href.includes('main.htm') ? '3D' : '2D')}
                        id='tool-open-in-3d'>
                        3D
                    </Button>
                </div>
                <TileLayersDropdown mapTileLayers={mapWrapper.tileLayers} />
                <Dropdown closeOnBodyClick={true}>
                    <i className='iki iki-more' />
                    <>
                        <div className='dropdown-list-title'>{__('general.actions')}:</div>
                        <ul className='dropdown-list clickable'>
                            <li onClick={this.activatePrintTool}>{__('js_map.map_image')}</li>
                            <li onClick={this.clearMapState}>{__('js_map.clear_map_state')}</li>
                        </ul>
                        <div className='dropdown-list-title'>{__('general.settings')}:</div>
                        <ul className='dropdown-list clickable'>
                            <li onClick={() => (mapTools.openCrossSectionInNewTab = !mapTools.openCrossSectionInNewTab)}>
                                <input type='checkbox' checked={mapTools.openCrossSectionInNewTab} readOnly />
                                {__('js_map.open_cross_section_in_new_tab')}
                            </li>
                        </ul>
                    </>
                </Dropdown>
            </>
        );
    }

    startApproval = () => {
        const tool = LogpointsEditor2dMap.selectForApprovalOnMap(mapWrapper.featureLayers.logpoints, mapRootStore.treeData).selectionTool;
        mapTools.activateTool(tool);
    };

    openIn3d = async () => {
        Telemetry.add(TelemetryEventType.MAP_2D_TOOLBAR_3D_VIEW, TelemetrySource.MAP_2D_DESKTOP);
        const tempState: IPageState = await mapStateFacade.createTemporaryState();
        PageStateUtils.openStateInOtherMap(tempState);
    };

    fitVisible = () => {
        Telemetry.add(TelemetryEventType.MAP_2D_TOOLBAR_FIT, TelemetrySource.MAP_2D_DESKTOP);

        const totalExtent = olExtent.createEmpty();
        const featureLayers = mapWrapper.featureLayers;

        [featureLayers.documents, featureLayers.images].filter(Boolean).forEach(layer => {
            try {
                olExtent.extend(totalExtent, layer.getExtentExcludeZero());
            } catch (e) {}
        });

        [featureLayers.alignments, featureLayers.logpoints, featureLayers.models, featureLayers.pipes, featureLayers.points, featureLayers.stringlines, featureLayers.vehicles]
            .filter(Boolean)
            .forEach(layer => {
                try {
                    olExtent.extend(totalExtent, layer.layerSource.getExtent());
                } catch (e) {}
            });

        const bgMapsExtent = mapWrapper.backgroundMaps.getLoadedDrawingExtents();

        if (!olExtent.isEmpty(bgMapsExtent)) {
            olExtent.extend(totalExtent, bgMapsExtent);
        }

        if (olExtent.isEmpty(totalExtent)) {
            Notify.warn(__('js_general.nothing_on_map') || 'Nothing to fit');
            return;
        }

        mapWrapper.fitExtent(totalExtent);
    };

    clearMapState = () => {
        const modelsPanel = mapUI.mapPanels.panels.models;
        // Same function as the Designs panel's own "Clear selection" button:
        // unloads every checked file per node and empties the tree store.
        // Runs before clearState() so a failure there can't leave the tree checked.
        modelsPanel?.clearSelection();

        mapStateFacade.handler.clearState();
        mapWrapper.featureLayers.logpoints.clear();
        mapWrapper.featureLayers.points.clear();
        mapUI.mapPanels.panels.documents?.clearSelection();
        mapUI.mapPanels.panels.images?.clearSelection();
        mapTools.areaTool.clear();
        mapTools.distanceTool.clear();
        Cache.clear();

        mapWrapper.featureLayers.alignments.clear();
        const defaultAlignment = kuura.getProjectAlignment();
        if (defaultAlignment?.id) {
            mapRootStore.setActiveAlignment(parseInt(defaultAlignment.id), 0);
        }

        const defaultMap = kuura.getProjectWorksiteMapModel();
        if (defaultMap?.id) {
            const node = modelsPanel.tree?.getById(parseInt(defaultMap.id));
            if (node) {
                modelsPanel.setModelLoaded(node.uuid, true);
            }
        }

        WindowManager.closeAll();
    };

    activatePrintTool = async () => {
        const map = mapWrapper.getInstance() as OlMap;

        const volumeLabels = mapTools.volumeTool.getOverlayLabels();
        volumeLabels?.forEach(lbl => {
            (lbl as any).type = 'volume';
        });
        let measureLabels: any[] = [...volumeLabels].map(l => {
            const el = $(l.getElement());
            return {
                name: el[0].innerHTML,
                center: [el.offset().left, el.offset().top],
                align: 'start',
                type: (l as any).type,
            };
        });

        const mapLabels: any[] = [];
        [...mapWrapper.featureLayers.models.layerSource.getFeatures(), ...mapWrapper.featureLayers.alignments.layerSource.getFeatures()].forEach(f => {
            const source = f.get('source');
            if (utils.getExtensionFromFilename(source?.modelName ?? '') === 'ifc') {
                // ifc usually has many small substructures, so draw just one label for the whole source file
                if (!mapLabels.find(c => c.modelId === source.modelId)) {
                    let extent = olExtent.createEmpty();
                    const features = mapWrapper.featureLayers.models.getFeaturesBySource({ modelId: source.modelId });
                    const geoms = new GeometryCollection(features.map(f => f.getGeometry()));
                    olExtent.extend(extent, featuresExtent(features));
                    mapLabels.push({
                        name: source.modelName,
                        modelId: source.modelId,
                        center: map.getPixelFromCoordinate(geoms.getClosestPoint(olExtent.getCenter(extent))),
                        align: 'center',
                    });
                }
            } else if (f.get('data').name) {
                const geom: Geometry = f.getGeometry();
                const center = olExtent.getCenter(geom.getExtent());
                const closestPoint = geom.getClosestPoint(center);
                mapLabels.push({
                    name: f.get('data').name,
                    center: map.getPixelFromCoordinate(closestPoint),
                    align: 'center',
                });
            }
        });

        DialogUtils.renderComponentBasic(
            __('js_map.map_image'),
            <SaveMapImageDialog
                map={map}
                mapLabels={mapLabels}
                measureLabels={measureLabels}
                logpointsColorMode={mapWrapper.featureLayers.logpoints.colorFunctionName}
                mapBounds={mapWrapper.getInstance().getView().calculateExtent()}
                mapDegrees={mapRotationDegrees(mapWrapper.getInstance())}
            />
        );
    };
}
